import { HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, type ApplicationConfig, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgChartsModule } from 'ng2-charts';

import { APP_DEFAULT_LANGUAGE, isLangInApplicationLanguages, MaloupeLocale } from '@malou-io/package-utils';

import { AuthContext } from ':core/context/auth.context';
import { AuthHttpService } from ':core/http-services/auth.http-service';
import { environment } from ':environments/environment';
import { OtherLocalStorageKey } from ':shared/enums';
import { defaultChartJsConfiguration } from ':shared/helpers';
import { IconModule } from ':shared/modules/icon.module';
import { getJwtTokenFromLocalStorage, removeJwtTokenFromLocalStorage } from ':shared/utils/jwt-token-local-storage-functions';
import { createUserIdentity } from ':shared/utils/manage-user-uuid';

import { routes } from './app.routes';

if (environment.production) {
    // PRODUCTION ENV
    enableProdMode();
}

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            BrowserModule,
            IconModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpBackend],
                },
                defaultLanguage: _getDefaultLanguage(),
            }),
            NgChartsModule.forRoot({
                // @ts-ignore // because defaults object is not deep partial (only partial at first level)
                defaults: defaultChartJsConfiguration,
                generateColors: true,
            })
        ),
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideAnimationsAsync(),
        {
            provide: APP_INITIALIZER,
            useFactory: appTranslationInitializerFactory,
            deps: [TranslateService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appTokenValidityInitializerFactory,
            deps: [AuthHttpService, AuthContext],
            multi: true,
        },
    ],
};

export function createTranslateLoader(httpBackend: HttpBackend): TranslateHttpLoader {
    return new TranslateHttpLoader(new HttpClient(httpBackend), '../assets/i18n/', `.json?cacheBuster=${new Date().getTime()}`);
}

export function appTranslationInitializerFactory(translate: TranslateService) {
    return (): Promise<any> =>
        new Promise<any>((resolve: any) => {
            const localStorageLang = localStorage.getItem(OtherLocalStorageKey.LANG);
            const availableLanguages: string[] = Object.values(MaloupeLocale);
            const langToSet = localStorageLang && availableLanguages.includes(localStorageLang) ? localStorageLang : APP_DEFAULT_LANGUAGE;
            translate.setDefaultLang(langToSet);
            translate.use(langToSet).subscribe({
                next: () => {
                    console.info(`Successfully initialized '${langToSet}' language.`);
                },
                error: (err) => {
                    console.error(`Problem with '${langToSet}' language initialization.`);
                    console.error(err);
                },
                complete: () => {
                    resolve(null);
                },
            });
        });
}

export function appTokenValidityInitializerFactory(authHttpService: AuthHttpService, authContext: AuthContext) {
    return (): Promise<any> =>
        new Promise<any>((resolve: any) => {
            const jwtToken = getJwtTokenFromLocalStorage();
            if (jwtToken) {
                authHttpService.isTokenValid$(jwtToken).subscribe({
                    next: (res) => {
                        authContext.isTokenValid.set(res.data);
                        createUserIdentity();
                    },
                    error: () => {
                        removeJwtTokenFromLocalStorage();
                    },
                    complete: () => {
                        resolve(null);
                    },
                });
            } else {
                createUserIdentity();
                resolve(null);
            }
        });
}

function _getDefaultLanguage(): string {
    const userLang = navigator.language;
    let lang = ['en', 'fr'].includes(userLang) ? userLang : APP_DEFAULT_LANGUAGE;
    let localStorageLang: string | null = null;
    try {
        localStorageLang = localStorage.getItem(OtherLocalStorageKey.LANG);
    } catch (error) {
        // We may have an error when trying to access local storage on some Firefox versions,
        // https://stackoverflow.com/questions/23269532/localstorage-in-firefox-extension-throws-the-operation-is-insecure-exception
        // or if the user has blocked cookies in Safari
        // https://stackoverflow.com/questions/46632093/getting-security-error-on-iphone-when-using-localstorage
        // It happened sometimes when the users scan a QR code or scan a NFC tag
        // https://malou.sentry.io/issues/5363619381
        console.error('Error while trying to get language from local storage', error);
    }

    if (localStorageLang && isLangInApplicationLanguages(localStorageLang)) {
        lang = localStorageLang;
    }

    return lang;
}
